"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PURCHASE_CREDIT_NOTE_SERIES = exports.PURCHASE_DEBIT_NOTE_SERIES = void 0;
exports.billOpenBalance = billOpenBalance;
exports.pricePurchaseNote = pricePurchaseNote;
exports.checkPurchaseNote = checkPurchaseNote;
exports.checkBillCancellation = checkBillCancellation;
exports.purchaseNoteIntent = purchaseNoteIntent;
const money_1 = require("../money");
const document_rules_1 = require("../posting/document-rules");
const invoice_1 = require("../sales/invoice");
const line_tax_1 = require("../tax/line-tax");
const bill_1 = require("./bill");
/** سلسلتا إشعارات المشتريات — مستقلتان عن فواتير الموردين وعن إشعارات المبيعات. */
exports.PURCHASE_DEBIT_NOTE_SERIES = 'PD';
exports.PURCHASE_CREDIT_NOTE_SERIES = 'PC';
/**
 * متبقى فاتورة المورد: الإجمالى + الدائنة − المدينة − المسدد، والملغاة صفر — قيدها
 * العكسى أعاد الذمة كاملة، ولا سداد ولا إشعار عليها أصلاً (شرط الإلغاء).
 */
function billOpenBalance(position) {
    if (position.cancelled)
        return money_1.Money.zero();
    return money_1.Money.of(position.total)
        .add(position.creditNotes)
        .sub(position.debitNotes)
        .sub(position.paid);
}
/**
 * تسعير الإشعار بنفس قاعدة الفاتورة، وضريبته بقاعدة نافذة فى تاريخ الفاتورة الأصلية:
 * الإشعار يصحح فاتورة صدرت بنسبتها — `BLOCKED-TAX` كإشعارات المبيعات.
 */
function pricePurchaseNote(lines, dates) {
    const issues = [];
    if (dates.noteDate < dates.billDate) {
        issues.push({
            code: 'NOTE_BEFORE_BILL',
            message: 'note date ' + dates.noteDate + ' is before bill date ' + dates.billDate,
        });
    }
    const priced = (0, invoice_1.priceInvoice)(lines, { issueDate: dates.billDate, dueDate: dates.billDate });
    if (!priced.ok)
        issues.push(...priced.issues);
    if (issues.length > 0 || !priced.ok)
        return { ok: false, issues };
    return priced;
}
/**
 * حدود الإشعار على فاتورته، كل المشاكل معًا:
 *  - لا إشعار على فاتورة ملغاة.
 *  - مجموع المدينة (بالضريبة) لا يتجاوز إجمالى الفاتورة ودائناتها.
 *  - ولا يتجاوز متبقيها: مرتجع على فاتورة مسددة يعنى استردادًا نقديًا من المورد أو
 *    رصيدًا مدينًا عليه، وكلاهما غير معتمد بعد (`BLOCKED-ACCOUNTING`) — فيُرفض.
 */
function checkPurchaseNote(kind, noteTotal, bill) {
    const issues = [];
    if (bill.cancelled) {
        issues.push({ code: 'BILL_CANCELLED', message: 'purchase bill is cancelled' });
        return issues;
    }
    if (kind === 'debit') {
        const ceiling = money_1.Money.of(bill.total).add(bill.creditNotes);
        if (money_1.Money.of(bill.debitNotes).add(noteTotal).gt(ceiling)) {
            issues.push({
                code: 'DEBIT_NOTE_EXCEEDS_BILL',
                message: 'debit notes would exceed the bill total including tax and credit notes (' +
                    ceiling.toFixed(2) +
                    ')',
            });
        }
        const open = billOpenBalance(bill);
        if (money_1.Money.of(noteTotal).gt(open)) {
            issues.push({
                code: 'DEBIT_NOTE_EXCEEDS_OPEN_BALANCE',
                message: 'debit note exceeds the open balance of the bill (' + open.toFixed(2) + ')',
            });
        }
    }
    return issues;
}
/** شروط إلغاء فاتورة المورد — الفترة المقفلة يرفضها محرك الترحيل نفسه بـ `PERIOD_CLOSED`. */
function checkBillCancellation(bill, cancellationDate) {
    const issues = [];
    if (bill.cancelled) {
        issues.push({ code: 'BILL_ALREADY_CANCELLED', message: 'purchase bill is already cancelled' });
        return issues;
    }
    if (money_1.Money.of(bill.paid).isPositive()) {
        issues.push({
            code: 'BILL_HAS_PAYMENTS',
            message: 'purchase bill has payments allocated to it; cancellation would orphan them',
        });
    }
    if (bill.noteCount > 0) {
        issues.push({
            code: 'BILL_HAS_NOTES',
            message: 'purchase bill has debit or credit notes; correct it by a debit note instead',
        });
    }
    if (cancellationDate < bill.billDate) {
        issues.push({
            code: 'CANCELLATION_BEFORE_BILL',
            message: 'cancellation date ' + cancellationDate + ' is before bill date ' + bill.billDate,
        });
    }
    return issues;
}
/**
 * قيد إشعار المشتريات.
 *
 * الدائن (من المورد) صورة فاتورة المورد: مدين المصروف وضريبة المدخلات، دائن الموردين.
 *
 * المدين عكسها: مدين الموردين بالإجمالى على الفاتورة، ودائن المردودات (أو حساب كل سطر
 * حسب الإعداد) بالصافى وبالضريبة غير القابلة للخصم — فهى حُمّلت على التكلفة أصلاً —
 * ودائن ضريبة المدخلات بالقابلة للخصم بأثر قاعدة كل سطر.
 */
function purchaseNoteIntent(ctx, input) {
    if (input.kind === 'credit') {
        return {
            ...(0, bill_1.purchaseBillIntent)(ctx, { ...input, bill: input.note }),
            sourceType: 'purchase_credit_note',
        };
    }
    const { note } = input;
    const costs = [];
    const taxLines = [];
    for (const line of note.lines) {
        let cost = line.net;
        // كفاتورة المورد: أثر القاعدة يبقى على سطر التكلفة حين لا يحمله سطر مدخلات
        let carried = line.tax?.snapshot ?? null;
        if (line.tax && line.tax.amount.isPositive()) {
            if (line.tax.snapshot.recoverable) {
                const account = input.inputTaxAccountOf?.({ ruleId: line.tax.snapshot.ruleId }) ?? ctx.accounts.inputTax;
                taxLines.push((0, document_rules_1.creditLine)(account, line.tax.amount, ctx.currency, { tax: line.tax.snapshot }));
                carried = null;
            }
            else {
                cost = cost.add(line.tax.amount);
            }
        }
        const account = input.debitNoteAccount === 'purchase_returns' &&
            input.purchaseReturnsAccount &&
            input.isExpenseAccount(line.account)
            ? input.purchaseReturnsAccount
            : line.account;
        costs.push({ account, amount: cost, tax: carried });
    }
    const lines = [
        (0, document_rules_1.debitLine)(ctx.accounts.payable, note.total, ctx.currency, {
            counterparty: { type: 'supplier', id: input.supplierId, allocationRef: input.billId },
        }),
    ];
    for (const cost of (0, line_tax_1.groupByAccountAndTax)(costs)) {
        if (cost.amount.isPositive()) {
            lines.push((0, document_rules_1.creditLine)(cost.account, cost.amount, ctx.currency, { tax: cost.tax }));
        }
    }
    lines.push(...taxLines);
    return {
        companyId: ctx.companyId,
        sourceType: 'purchase_debit_note',
        sourceId: input.sourceId,
        postingDate: input.postingDate,
        idempotencyKey: input.idempotencyKey,
        reference: input.reference ?? null,
        memo: input.memo ?? null,
        createdBy: ctx.createdBy ?? null,
        lines,
    };
}
//# sourceMappingURL=notes.js.map